/* eslint-disable */
import {
  SHOWPASS_ANALYTICS_SIGNATURE_KEY,
  X_SP_ANALYTICS_SIGNATURE,
} from "@shared/env";
import { checkForReactNativeAttribute } from "@shared/hooks/platform/react-native";
import { PublicInternalAnalyticsTrackingSerializer } from "@shared/modules/analytics/types";
import isEmpty from "lodash/isEmpty";
import { compute_insecure_signature } from "@shared/utils/crypto/utils";
import url from "@shared/utils/urls/url";
import { IAnalyticsClient } from "./IAnalyticsClient";

/**
 * ShowpassAnalyticsClient is responsible for sending analytics events to the Showpass backend.
 * It implements the IAnalyticsClient interface and handles:
 *
 * - Signing payloads with a signature for security verification
 * - Adding necessary headers including CSRF tokens and cookies
 * - Enriching events with context like app source and URL
 * - Sending events via fetch with keepalive enabled
 */
export class ShowpassAnalyticsClient implements IAnalyticsClient {
  /**
   * Signs the analytics payload using an insecure signature for basic verification.
   * @param payload - The analytics event data to sign
   * @returns Promise resolving to the base64 encoded signature string
   */
  private async signPayload(
    payload: PublicInternalAnalyticsTrackingSerializer
  ): Promise<string> {
    const signedPayload = await compute_insecure_signature(
      payload,
      SHOWPASS_ANALYTICS_SIGNATURE_KEY
    );
    return signedPayload instanceof ArrayBuffer
      ? btoa(String.fromCharCode(...new Uint8Array(signedPayload)))
      : "";
  }

  /**
   * Builds the request headers including content type, analytics signature, and CSRF token.
   * @param signedPayload - The signed payload string to include in headers
   * @returns Object containing the headers
   */
  private getHeaders(signedPayload: string): { [key: string]: string } {
    const cookie =
      document && "cookie" in document
        ? Object.fromEntries(
            document.cookie
              .split("; ")
              .map((v) => v.split(/=(.*)/s).map(decodeURIComponent))
          )
        : {};
    const headers: { [key: string]: string } = {
      "Content-Type": "application/json",
    };
    if (signedPayload) {
      headers[X_SP_ANALYTICS_SIGNATURE] = signedPayload;
    }
    if (cookie.csrftoken) {
      headers["X-CSRFTOKEN"] = cookie.csrftoken;
    }
    return headers;
  }

  /**
   * Sends an analytics event to the backend.
   * @param payload - The analytics event data to send
   * @returns Promise resolving to the fetch Response or null if the request fails
   */
  public async send(
    payload: PublicInternalAnalyticsTrackingSerializer
  ): Promise<Response | null> {
    const { interaction_type, ...rest } = payload;
    // Don't send if payload is empty aside from interaction_type
    if (isEmpty(rest)) return null;

    // Enrich with context
    payload.app_source = checkForReactNativeAttribute()
      ? "React Native"
      : "NextJS";
    payload.requested_url = window.location.href;
    payload.referrer = document.referrer;

    try {
      const signedPayloadString = await this.signPayload(payload);
      const headers = this.getHeaders(signedPayloadString);
      const response = await fetch(url("api/public/analytics/internal/add/"), {
        keepalive: true, // Ensures request completes even if page changes
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
        cache: "no-store",
      });
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    }
  }
}
